import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { styles, levels } from "./components/PromptConfigurator";
import AppHeader from "./components/AppHeader";
import PromptConfigurator from "./components/PromptConfigurator";
import RedirectPopup from "./components/RedirectPopup";
import TagStudyPopup from "./components/TagStudyPopup";
import {
  trackSessionLoad,
  trackSessionError,
  trackSectionClick,
  trackTopicClick,
  trackSectionBack,
  trackRedirectPopupShow,
  trackChatGPTRedirect,
  trackPromptOptionUsage,
  trackPromptStyleUsage,
  trackPromptLevelUsage,
  trackPromptConfigOpen,
} from "./utils/analytics";

function App() {
  const [session, setSession] = useState(null);
  const [error, setError] = useState(false);
  const [showRedirectPopup, setShowRedirectPopup] = useState(false);
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [promptOptions, setPromptOptions] = useState(() => {
    const saved = localStorage.getItem("promptOptions");
    const defaults = {
      summary: true,
      keyPoints: false,
      article: false,
      quotes: false,
      table: false,
      diagram: true,
      shortSummary: false,
      questions: false,
      flashcards: false,
      faq: false,
      qa: false,
    };
    return saved ? JSON.parse(saved) : defaults;
  });
  const [promptStyle, setPromptStyle] = useState(() => {
    return localStorage.getItem("promptStyle") || "neutral";
  });
  const [promptLevel, setPromptLevel] = useState(() => {
    return localStorage.getItem("promptLevel") || "intermediate";
  });
  const [research, setResearch] = useState(() => {
    return localStorage.getItem("research") || "none";
  });
  const { sessionId } = useParams();
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved
      ? JSON.parse(saved)
      : window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const isMac = /Mac/.test(navigator.userAgent);
  const pasteShortcut = isMac ? "⌘+V" : "Ctrl+V";

  function generatePrompt(topic) {
    const options = [];
    if (promptOptions.summary) options.push("summary (default structure)");
    if (promptOptions.shortSummary) options.push("short summary (1 paragraph)");
    if (promptOptions.article) options.push("article (full article)");
    if (promptOptions.quotes) options.push("quotes");
    if (promptOptions.table) options.push("table");
    if (promptOptions.keyPoints) options.push("key points (bullet points)");
    if (promptOptions.diagram) options.push("diagram");
    if (promptOptions.questions) options.push("questions (quiz session)");
    if (promptOptions.flashcards) options.push("flashcards");
    if (promptOptions.faq) options.push("faq");
    if (promptOptions.qa) options.push("questions & answers");

    if (options.length === 0) return `${topic}`;

    let prompt = `Using a ${styles[
      promptStyle
    ].toLowerCase()} style and targeting a ${levels[
      promptLevel
    ].toLowerCase()} level audience, create the following content titled "${topic}". Position yourself (without mentioning it) in the area of ${
      session.title
    }, ${currentSection}):\n`;

    options.forEach((option) => {
      prompt += `• ${option}\n`;
    });

    return prompt;
  }

  function trackPromptConfiguration() {
    // Track each selected option
    Object.entries(promptOptions).forEach(([option, isEnabled]) => {
      if (isEnabled) {
        trackPromptOptionUsage(option);
      }
    });

    // Track style and level
    trackPromptStyleUsage(promptStyle);
    trackPromptLevelUsage(promptLevel);
  }

  function handleTopicClick(topic) {
    if (!topic.isLeaf) {
      setCurrentSection(topic.name);
      const section = session.sections.find((s) => s.name === topic.name);
      trackSectionClick(sessionId, topic.name, section.topics.length);
      return;
    }

    // Track topic click
    trackTopicClick(sessionId, currentSection, topic.name);

    // Track prompt configuration usage
    trackPromptConfiguration();

    const prompt = generatePrompt(topic.name);
    const shouldShowPopup =
      localStorage.getItem("hideRedirectPopup") !== "true";

    if (shouldShowPopup) {
      trackRedirectPopupShow(sessionId, topic.name);
      setShowRedirectPopup(true);
      localStorage.setItem("lastPrompt", prompt);
    } else {
      navigator.clipboard.writeText(prompt);
      trackChatGPTRedirect(sessionId, topic.name, true);
      window.open("https://chatgpt.com/g/g-uefFoRnpX", "_blank");
    }
  }

  function handleRedirectConfirm(dontShowAgain) {
    if (dontShowAgain) {
      localStorage.setItem("hideRedirectPopup", "true");
    }
    setShowRedirectPopup(false);
    const prompt = localStorage.getItem("lastPrompt");
    navigator.clipboard.writeText(prompt);
    trackChatGPTRedirect(sessionId, currentSection, false);
    window.open("https://chatgpt.com/g/g-uefFoRnpX", "_blank");
  }

  function generateTagStudyPrompt(tag) {
    const otherTags = session.tags.filter((t) => t !== tag).join(", ");
    const context = otherTags
      ? `${session.title} and other topics like ${otherTags}`
      : session.title;

    return `While exploring ${context}, I've become interested in studying "${tag}" in depth. Could you create a comprehensive study session titled "${tag}" that breaks down everything one needs to know about ${tag} into well-organized sections and topics? Please structure it as a complete learning path.`;
  }

  function handleTagClick(tag) {
    const prompt = generateTagStudyPrompt(tag);
    const shouldShowPopup = localStorage.getItem("hideTagPopup") !== "true";
    if (shouldShowPopup) {
      setSelectedTag(tag);
      localStorage.setItem("lastTagPrompt", prompt);
      setShowTagPopup(true);
    } else {
      navigator.clipboard.writeText(prompt);
      window.open("https://chatgpt.com/g/g-uefFoRnpX", "_blank");
    }
  }

  function handleTagPopupConfirm(dontShowAgain) {
    if (dontShowAgain) {
      localStorage.setItem("hideTagPopup", "true");
    }
    setShowTagPopup(false);
    const prompt = localStorage.getItem("lastTagPrompt");
    navigator.clipboard.writeText(prompt);
    window.open("https://chatgpt.com/g/g-uefFoRnpX", "_blank");
  }

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    if (sessionId) {
      fetch("https://index-api.widenex.com/session/" + sessionId, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Session expired");
          return res.json();
        })
        .then((data) => {
          setSession(data);
          trackSessionLoad(
            sessionId,
            data.title,
            data.sections.length,
            data.sections.reduce(
              (acc, section) => acc + section.topics.length,
              0
            )
          );
        })
        .catch((error) => {
          setError(true);
          trackSessionError(sessionId, "session_expired");
        });
    }
  }, [sessionId]);

  function TopicBrowser({ onSelect }) {
    if (!session) return null;

    const topics = currentSection
      ? session.sections
          .find((s) => s.name === currentSection)
          .topics.map((topic) => ({
            name: topic,
            isLeaf: true,
          }))
      : session.sections.map((section) => ({
          name: section.name,
          count: section.topics.length,
        }));

    return (
      <div
        className={`duration-300 transition-all rounded-xl ${
          !currentSection ? "bg-transparent p-0" : "bg-black/10 p-4"
        }`}
      >
        {currentSection && (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => {
                trackSectionBack(sessionId, currentSection);
                setCurrentSection(null);
              }}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-2 text-gray-700 dark:text-gray-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span className="text-sm sm:text-base">Back</span>
            </button>
            <h2 className="text-lg ml-5 text-left flex-1 leading-5 sm:text-xl font-bold text-gray-700 dark:text-gray-300">
              {currentSection}
            </h2>
          </div>
        )}
        <div className="grid gap-3 sm:gap-4 max-h-[70vh] overflow-y-auto pr-4 custom-scrollbar">
          <div className="p-1 pl-2 transform-gpu">
            {topics.map((topic, index) => (
              <button
                key={index}
                onClick={() => onSelect(topic)}
                className={`text-left p-4 sm:p-6 rounded-xl transition-all transform hover:scale-[1.02] w-full mb-3 sm:mb-4
                  ${
                    topic.isLeaf
                      ? "bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-700/50 hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700/50 dark:hover:to-gray-600/50 cursor-pointer"
                      : "bg-gradient-to-br from-white to-gray-50 dark:from-gray-700 dark:to-gray-800 hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-600 dark:hover:to-gray-700 cursor-pointer"
                  }`}
              >
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center justify-between">
                  <span>{topic.name}</span>
                  {!topic.isLeaf ? (
                    <span className="hidden md:inline text-sm text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-900/50 px-3 py-1 rounded-full">
                      {topic.count} topics
                    </span>
                  ) : (
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  )}
                </h3>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    if (error || !sessionId) {
      return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
          <div className="max-w-md w-full mx-auto p-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center">
              <div className="text-red-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                Session Expired
              </h2>

              <p className="text-gray-600 dark:text-gray-300 mb-8">
                This session has expired. Download our mobile app to save your
                sessions and study offline!
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://apps.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img
                    src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us"
                    alt="Download on the App Store"
                    className="h-12"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800">
      <AppHeader
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        onConfigClick={() => {
          trackPromptConfigOpen();
          setShowConfig(true);
        }}
        promptPreview={
          <PromptConfigurator
            promptOptions={promptOptions}
            setPromptOptions={setPromptOptions}
            promptStyle={promptStyle}
            setPromptStyle={setPromptStyle}
            promptLevel={promptLevel}
            setPromptLevel={setPromptLevel}
            isOpen={showConfig}
            onClose={() => setShowConfig(false)}
          />
        }
      />

      <div className="pt-20 sm:pt-24 px-3 sm:px-6 md:px-10">
        <div className="text-center flex flex-col gap-4 sm:gap-6 items-center justify-center max-w-4xl mx-auto print:p-0 print:max-w-none">
          <div className="w-full max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-8 print:p-0 print:shadow-none">
            {session.title && (
              <div>
                <h1 className="text-2xl sm:text-2xl md:text-3xl font-bold text-gray-800 dark:text-white mb-4 sm:mb-6 text-center">
                  {session.title}
                </h1>
                {session.tags && session.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center mb-4 sm:mb-6">
                    {session.tags.map((tag, index) => (
                      <button
                        key={index}
                        onClick={() => handleTagClick(tag)}
                        className="px-2 sm:px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs sm:text-sm text-gray-600 dark:text-gray-300 hover:bg-opacity-75 dark:hover:bg-opacity-75 transform hover:scale-105 transition-all duration-200 cursor-pointer"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <TopicBrowser onSelect={handleTopicClick} />
          </div>
        </div>
      </div>

      <RedirectPopup
        isOpen={showRedirectPopup}
        onClose={() => setShowRedirectPopup(false)}
        onConfirm={handleRedirectConfirm}
        pasteShortcut={pasteShortcut}
        sessionId={sessionId}
        topicName={currentSection}
        promptConfig={{
          options: promptOptions,
          style: promptStyle,
          level: promptLevel,
        }}
      />

      <TagStudyPopup
        isOpen={showTagPopup}
        onClose={() => setShowTagPopup(false)}
        onConfirm={handleTagPopupConfirm}
        pasteShortcut={pasteShortcut}
        selectedTag={selectedTag}
      />
    </div>
  );
}

export default App;
